(() => {
  const theme = (window.theme = window.theme || {});
  if (theme.Quantity) return;

  const DESIGN_VALIDATE_PATH = '/apps/jam-designer/api/design/validate';

  const toCents = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  };

  const asArray = (value) => (Array.isArray(value) ? value : []);

  const parseJsonArray = (raw) => {
    if (Array.isArray(raw)) return raw;
    try {
      return asArray(JSON.parse(raw || '[]'));
    } catch (error) {
      return [];
    }
  };

  const byQty = (a, b) => a.qty - b.qty;

  const parseTiers = (raw) =>
    parseJsonArray(raw)
      .map((tier) => ({
        qty: Number(tier.qty) || 0,
        price: Number(tier.price) || 0,
        compare: Number(tier.compare) || 0,
        reduced: Boolean(tier.reduced),
        percentOff: Number(tier.percentOff) || 0,
      }))
      .sort(byQty);

  const priceCurveToTiers = (priceCurve = []) =>
    asArray(priceCurve)
      .map((entry) => ({
        qty: Number(entry?.qty) || 0,
        price: toCents(entry?.price?.unitPrice),
        compare: 0,
        reduced: false,
      }))
      .filter((tier) => tier.qty > 0)
      .sort(byQty);

  const quantitiesToPriceMap = (quantities = []) =>
    asArray(quantities).reduce((map, entry) => {
      const qty = Number(entry?.qty) || 0;
      if (qty <= 0) return map;

      const totalCents = toCents(entry?.price?.total);
      const unitCents = (totalCents > 0 ? Math.round(totalCents / qty) : 0) || toCents(entry?.price?.unitPrice);
      return map.set(qty, { unitPriceCents: unitCents, totalPriceCents: totalCents || unitCents * qty });
    }, new Map());

  const parseSteps = parseJsonArray;

  const parseBands = (steps) =>
    parseSteps(steps)
      .map((entry) => ({ start: Number(entry?.startingAtQty) || 0, step: Number(entry?.requiredStepIncrement) || 0 }))
      .filter((band) => band.start > 0 && band.step > 0)
      .sort((a, b) => a.start - b.start)
      .map((band, index, sorted) => ({ ...band, end: sorted[index + 1]?.start ?? Infinity }));

  const stepBands = (steps, increment = 1) => {
    const bands = parseBands(steps);
    return bands.length ? bands : [{ start: 1, step: Math.max(1, Number(increment) || 1), end: Infinity }];
  };

  const stepIncrementForQuantity = (quantity, stepIncrements = []) => {
    const qty = Number(quantity) || 0;
    return parseBands(stepIncrements).find((band) => qty >= band.start && qty < band.end)?.step || 0;
  };

  const rulesFromDataset = (dataset = {}) => ({
    min: Number(dataset.minQty) || 1,
    increment: Number(dataset.increment) || 1,
    max: Number(dataset.maxQty) || 0,
    steps: parseSteps(dataset.steps),
  });

  const incrementForQuantity = (quantity, { increment, steps } = {}) =>
    stepIncrementForQuantity(quantity, steps) || Number(increment) || 1;

  const raiseQuantity = (value, { increment, min, steps } = {}) => {
    const floor = Math.max(Number(value) || 0, Number(min) || 1);
    const bands = stepBands(steps, increment);
    if (floor < bands[0].start) return floor;

    return Math.min(
      ...bands.flatMap((band) => {
        const raised = Math.ceil(Math.max(floor, band.start) / band.step) * band.step;
        return band.end > floor && raised < band.end ? [raised] : [];
      })
    );
  };

  const lowerQuantity = (value, { increment, min, steps } = {}) => {
    const quantity = Number(value) || 0;
    const bands = stepBands(steps, increment);
    const lowered = Math.max(
      Math.min(quantity, bands[0].start - 1),
      ...bands.flatMap((band) => {
        const floored = Math.floor(Math.min(quantity, band.end - 1) / band.step) * band.step;
        return band.start <= quantity && floored >= band.start ? [floored] : [];
      })
    );

    return lowered >= (Number(min) || 1) ? lowered : undefined;
  };

  const snapQuantity = (value, { increment, min, max = 0, steps } = {}) => {
    const rules = { increment, min, steps };
    const raised = raiseQuantity(value, rules);
    const maxQty = Number(max) || 0;
    if (maxQty <= 0 || raised <= maxQty) return raised;

    return lowerQuantity(maxQty, rules) ?? maxQty;
  };

  const tierForQuantity = (quantity, tiers = []) =>
    tiers.reduce((active, tier) => (quantity >= tier.qty ? tier : active), undefined);

  const unitPriceForQuantity = (quantity, tiers = [], basePrice = 0) =>
    tierForQuantity(quantity, tiers)?.price ?? basePrice;

  const fetchDesignValidation = async ({ designId, sku, quantities } = {}) => {
    if (!designId || !sku) return undefined;

    try {
      const response = await fetch(DESIGN_VALIDATE_PATH, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          designId: String(designId),
          sku: String(sku),
          quantities: Array.isArray(quantities) ? quantities : undefined,
        }),
      });
      if (!response.ok) return undefined;

      const body = await response.json();
      const trustworthy = body && body.isValid !== false && Array.isArray(body.priceCurve);
      return trustworthy ? body : undefined;
    } catch (error) {
      console.warn('[Quantity] design validate failed', error);
      return undefined;
    }
  };

  const formatMoney = (cents) => {
    if (theme.Currency?.formatMoney) {
      const format = theme.settings?.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings?.moneyFormat;
      return theme.Currency.formatMoney(cents, format);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  };

  const formatAtEach = (unitPriceCents, template = 'at {{ price }} each') =>
    template.replace(/\{\{\s*price\s*\}\}/, formatMoney(unitPriceCents));

  const customHintMessage = ({ isAdjusted, adjustedLabel, hintLabel, increment } = {}) => {
    if (isAdjusted) return adjustedLabel?.replace('[increment]', increment) || `Adjusted to multiples of ${increment}.`;
    return hintLabel || 'Multiples of';
  };

  theme.Quantity = Object.freeze({
    DESIGN_VALIDATE_PATH,
    parseTiers,
    unitPriceToCents: toCents,
    totalPriceToCents: toCents,
    priceCurveToTiers,
    quantitiesToPriceMap,
    stepIncrementForQuantity,
    fetchDesignValidation,
    parseSteps,
    rulesFromDataset,
    incrementForQuantity,
    stepBands,
    raiseQuantity,
    lowerQuantity,
    snapQuantity,
    tierForQuantity,
    unitPriceForQuantity,
    formatMoney,
    formatAtEach,
    customHintMessage,
  });
})();
